// @ts-strict-ignore
import { Injectable } from "@angular/core";
import { LogService } from "src/app/shared/services/log.service";
import { ExpertModeElement, ExpertModeElementScreen, ExpertModeElementScreenInput } from "../../components/expert/expert.component";
import {
  ProfileElementConditionType,
  ProfileElementScopeType,
  ProfileElementScreenInputValidator,
  ProfileElementScreenInputValidatorCondition,
  ProfileValidityType
} from "../models/profile-element.model";

export interface ExpertValidatorServiceResult {
  valid: boolean;
  validityType: ProfileValidityType;
  errorMessage?: string | null;
}

export interface ExpertValidatorServiceInputScopes {
  globalScope: any;
  elementScope: any;
  screenScope: any;
  arrayScope: any;
  scope: any;
}

@Injectable({ providedIn: "root" })
export class ExpertValidatorService {
  debugMode = false;
  onlyDebugKey: string | null = null;
  private logService: LogService;

  validateInput(input: ExpertModeElementScreenInput, elements: ExpertModeElement[]): ExpertValidatorServiceResult {
    this.checkForDebugging(input.key, true);
    // If an input is not enabled, it is valid by default
    if (!this.enableCheckInput(input, elements)) {
      return { valid: true, validityType: "notenabled" };
    }
    if (!input.decorators?.required && input.value == null) {
      return { valid: true, errorMessage: null, validityType: "complete" };
    }

    if (input.incompleters?.length > 0) {
      const incompletersResult = this.processInputValidators(input, elements, input.incompleters);

      if (!incompletersResult.valid) {
        return { valid: false, validityType: "incomplete" };
      }
    }

    if (this.debugMode) console.warn("input incompleters passed, seeing if it is valid...");

    if (input.validators?.length > 0) {
      const validatorsResult = this.processInputValidators(input, elements, input.validators);

      if (!validatorsResult.valid) {
        return { valid: false, validityType: "error", errorMessage: validatorsResult.errorMessage };
      }
    }

    if (this.debugMode) console.warn("input validators passed, input is deemed complete...");

    return { valid: true, validityType: "complete" };
  }

  enableCheckInput(input: ExpertModeElementScreenInput, elements: ExpertModeElement[]): boolean {
    this.checkForDebugging(input.key);

    let isEnabled = true;

    const scope = this.getInputScope(input, elements, "screen");
    let isScreenEnabled = null;
    try {
      isScreenEnabled = this.enableCheckScreen(scope.screenScope[0], elements);
    } catch (e) {
      // swallow
    }

    // Only perform enable check on the input itself if the screen the input is on is enabled
    if (isScreenEnabled) {
      if (input.enablers?.length > 0) {
        const validationResult = this.processInputValidators(input, elements, input.enablers);

        if (validationResult.valid) {
          isEnabled = true;
        } else {
          isEnabled = false;
        }
      }
    } else {
      isEnabled = false;
    }

    if (this.debugMode) {
      if (isEnabled) {
        console.warn("input is enabled");
      } else {
        console.warn("input is not enabled");
      }
    }

    return isEnabled;
  }

  enableCheckScreen(screen: ExpertModeElementScreen, elements: ExpertModeElement[]): boolean {
    this.checkForDebugging(screen.key);

    let isEnabled = true;

    if (screen.enablers?.length > 0) {
      const validationResult = this.processInputValidators(
        { key: screen.key, instanceId: screen.instanceId, propertyName: null, type: null, enablers: screen.enablers },
        elements,
        screen.enablers
      );

      if (validationResult.valid) {
        isEnabled = true;
      } else {
        isEnabled = false;
      }
    }

    if (this.debugMode) {
      if (isEnabled) {
        console.warn("screen is enabled");
      } else {
        console.warn("screen is not enabled");
      }
    }

    return isEnabled;
  }

  processInputValidators(
    input: ExpertModeElementScreenInput,
    elements: ExpertModeElement[],
    validators: ProfileElementScreenInputValidator[]
  ) {
    if (validators?.length > 0) {
      let errorMessage = null;

      for (const validator of validators) {
        let allStatementsPass = true;

        for (const statement of validator.conditions) {
          const inputScope = this.getInputScope(input, elements, statement.scope);
          const elementsScope = inputScope.scope;
          const inputArrayScope = inputScope.arrayScope;

          let selfEquateValue = null;

          if (input.type === "array" || input.type === "object") {
            selfEquateValue = input.value?.length ?? 0;
          } else {
            if ((input.type === "string" || input.type === "code") && statement.condition.includes("SELF_LENGTH_")) {
              selfEquateValue = input.value?.length ?? 0;
            } else {
              selfEquateValue = input.value;
            }
          }

          if (statement.condition === "JSON_VALID" || statement.condition === "JSON_INVALID") {
            let validJSON = false;

            try {
              if (JSON.parse(this.removeComments(selfEquateValue))) {
                validJSON = true;
              }
            } catch (e) {
              validJSON = false;
            }

            if ((statement.condition === "JSON_VALID" && !validJSON) || (statement.condition === "JSON_INVALID" && validJSON)) {
              if (this.debugMode) console.warn("condition has failed, thus validation block has failed");

              allStatementsPass = false;

              // Only push out the first error message we have encountered
              if (!errorMessage && validator.errorMessage) {
                errorMessage = validator.errorMessage;
              }
            }
          } else if (statement.condition === "REGEXP_PASSES" || statement.condition === "REGEXP_FAILS") {
            const regExpPass = statement.value.test(selfEquateValue);

            if ((statement.condition === "REGEXP_PASSES" && !regExpPass) || (statement.condition === "REGEXP_FAILS" && regExpPass)) {
              if (this.debugMode) console.warn("condition has failed, thus validation block has failed");

              allStatementsPass = false;

              // Only push out the first error message we have encountered
              if (!errorMessage && validator.errorMessage) {
                errorMessage = validator.errorMessage;
              }
            }
          } else if (
            statement.condition === "ENABLED" ||
            statement.condition === "NOT_ENABLED" ||
            statement.condition === "VALID" ||
            statement.condition === "NOT_VALID"
          ) {
            const objectForKey = this.getObjectByKey(statement.key, elementsScope);
            let conditionFailed = false;

            if (statement.condition === "ENABLED") {
              const isEnabled = this.enableCheckInput(objectForKey, elements);
              this.checkForDebugging(input.key); // Check if we need to renable debugging if it got turned off by the check of the target's key

              if (!isEnabled) {
                if (this.debugMode) console.warn("condition failed, target needed to be ENABLED but is NOT_ENABLED");
                conditionFailed = true;
              }
            } else if (statement.condition === "NOT_ENABLED") {
              const isEnabled = this.enableCheckInput(objectForKey, elements);
              this.checkForDebugging(input.key); // Check if we need to renable debugging if it got turned off by the check of the target's key

              if (isEnabled) {
                if (this.debugMode) console.warn("condition failed, target needed to be NOT_ENABLED but is ENABLED");
                conditionFailed = true;
              }
            } else if (statement.condition === "VALID") {
              const isValid = this.validateInput(objectForKey, elements).valid;
              this.checkForDebugging(input.key); // Check if we need to renable debugging if it got turned off by the check of the target's key

              if (!isValid) {
                if (this.debugMode) console.warn("condition failed, target needed to be VALID but is NOT_VALID");
                conditionFailed = true;
              }
            } else if (statement.condition === "NOT_VALID") {
              const isValid = this.validateInput(objectForKey, elements).valid;
              this.checkForDebugging(input.key); // Check if we need to renable debugging if it got turned off by the check of the target's key

              if (isValid) {
                if (this.debugMode) console.warn("condition failed, target needed to be NOT_VALID but is VALID");
                conditionFailed = true;
              }
            }

            if (conditionFailed) {
              allStatementsPass = false;

              // Only push out the first error message we have encountered
              if (!errorMessage && validator.errorMessage) {
                errorMessage = validator.errorMessage;
              }
            }
          } else if (
            statement.condition === "SELF_EQUATES_TO" ||
            statement.condition === "SELF_NOT_EQUATES_TO" ||
            statement.condition.includes("SELF_LENGTH_")
          ) {
            const objectForKey = this.getObjectByKey(statement.key, elementsScope);

            if (objectForKey) {
              if (
                ((statement.condition === "SELF_EQUATES_TO" || statement.condition === "SELF_LENGTH_EQUAL") &&
                  selfEquateValue !== this.parseMathValue(objectForKey.value, statement)) ||
                ((statement.condition === "SELF_NOT_EQUATES_TO" || statement.condition === "SELF_LENGTH_NOT_EQUAL") &&
                  selfEquateValue === this.parseMathValue(objectForKey.value, statement))
              ) {
                if (this.debugMode) console.warn("condition has failed, thus validation block has failed");

                allStatementsPass = false;

                // Only push out the first error message we have encountered
                if (!errorMessage && validator.errorMessage) {
                  errorMessage = validator.errorMessage;
                }
              } else {
                let statementPasses = true;

                if (
                  statement.condition === "SELF_LENGTH_GREATER_THAN" &&
                  selfEquateValue <= this.parseMathValue(objectForKey.value, statement)
                ) {
                  statementPasses = false;
                } else if (
                  statement.condition === "SELF_LENGTH_GREATER_THAN_OR_EQUAL" &&
                  selfEquateValue < this.parseMathValue(objectForKey.value, statement)
                ) {
                  statementPasses = false;
                } else if (
                  statement.condition === "SELF_LENGTH_LESS_THAN" &&
                  selfEquateValue >= this.parseMathValue(objectForKey.value, statement)
                ) {
                  statementPasses = false;
                } else if (
                  statement.condition === "SELF_LENGTH_LESS_THAN_OR_EQUAL" &&
                  selfEquateValue > this.parseMathValue(objectForKey.value, statement)
                ) {
                  statementPasses = false;
                }

                if (!statementPasses) {
                  if (this.debugMode) console.warn("condition has failed, thus validation block has failed");

                  allStatementsPass = false;

                  // Only push out the first error message we have encountered
                  if (!errorMessage && validator.errorMessage) {
                    errorMessage = validator.errorMessage;
                  }
                }
              }
            }
          } else if (statement.condition === "IS_DISTINCT") {
            const distinct = this.isDistinct(input, elementsScope, statement.key ?? null);

            if (!distinct) {
              allStatementsPass = false;

              // Only push out the first error message we have encountered
              if (!errorMessage && validator.errorMessage) {
                errorMessage = validator.errorMessage;
              }
            }
          } else {
            let targetKey = null;
            let targetValue = null;
            let targetInstanceId = false;

            if (statement.key) {
              targetKey = statement.key;
            } else {
              // If key is not defined and the input is within an array, we have to use the instanceId to grab its unique input across all the array's records
              if (inputArrayScope) {
                targetKey = input.instanceId;
                targetInstanceId = true;
              } else {
                targetKey = input.key;
              }
            }

            const objectForKey = this.getObjectByKey(targetKey, elementsScope, targetInstanceId);

            if (objectForKey?.value === null) {
              targetValue = null;
            } else if (objectForKey?.value === "") {
              targetValue = "";
            } else if (objectForKey?.value) {
              targetValue = objectForKey.value;
            } else {
              targetValue = objectForKey;
            }

            if (!this.checkCondition(targetValue, statement.condition, statement?.value)) {
              allStatementsPass = false;

              // Only push out the first error message we have encountered
              if (!errorMessage && validator.errorMessage) {
                errorMessage = validator.errorMessage;
              }

              break;
            }
          }
        }

        // The first complete statement to pass means the input is valid
        if (allStatementsPass) {
          if (this.debugMode) console.warn("all conditions passed, thus validation block passed, validation SUCCESS");

          return { valid: true, errorMessage: null };
        }
      }

      if (this.debugMode) this.logService.log("no validation blocks have passed, validation FAILED");

      // If there are validators and none of them caused a pass then this fails
      return { valid: false, errorMessage };
    }

    // If there are no validators then this passes by default
    return { valid: true, errorMessage: null };
  }

  parseMathValue(value: number, statement: ProfileElementScreenInputValidatorCondition) {
    let mathValue = null;

    if (!statement.valueMathOperator || statement.valueMathOperator === "add") {
      mathValue = value + (statement.value ?? 0);
    } else if (statement.valueMathOperator === "subtract") {
      mathValue = value - (statement.value ?? 0);
    } else if (statement.valueMathOperator === "multiply") {
      mathValue = value * (statement.value ?? 0);
    } else if (statement.valueMathOperator === "divide") {
      mathValue = value / (statement.value ?? 0);
    }

    if (statement.valueMathRounding === "round") {
      mathValue = Math.round(mathValue);
    } else if (statement.valueMathRounding === "ceil") {
      mathValue = Math.ceil(mathValue);
    } else if (statement.valueMathRounding === "floor") {
      mathValue = Math.floor(mathValue);
    }

    return mathValue;
  }

  resolveInputScopeNested(instanceIdTarget: string, input: ExpertModeElementScreenInput) {
    if (input.value) {
      for (const record of input.value) {
        for (const arrayInput of record) {
          if (instanceIdTarget === arrayInput.instanceId) {
            return input.value;
          }

          if (arrayInput.type === "array" || arrayInput.type === "object") {
            const scope = this.resolveInputScopeNested(instanceIdTarget, arrayInput);

            if (scope) {
              return scope;
            }
          }
        }
      }
    }
  }

  checkCondition(target: any, condition: ProfileElementConditionType, value: any) {
    switch (condition) {
      case "EQUAL": {
        return target == value; // Specifically avoiding type checking (ie: ===)
      }
      case "NOT_EQUAL": {
        return target != value; // Specifically avoiding type checking (ie: !==)
      }
      case "GREATER_THAN": {
        return target > value;
      }
      case "GREATER_THAN_OR_EQUAL": {
        return target >= value;
      }
      case "LESS_THAN": {
        return target < value;
      }
      case "LESS_THAN_OR_EQUAL": {
        return target <= value;
      }
      case "EXISTS": {
        if (target) {
          return true;
        } else {
          return false;
        }
      }
      case "NOT_EXISTS": {
        if (!target) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_EQUAL": {
        if (target?.length === value) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_NOT_EQUAL": {
        if (target?.length !== value) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_GREATER_THAN": {
        if (target?.length > value) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_GREATER_THAN_OR_EQUAL": {
        if (target?.length >= value) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_LESS_THAN": {
        if (target?.length < value) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_LESS_THAN_OR_EQUAL": {
        if (target?.length <= value) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_EVEN": {
        if (target?.length % 2 === 0) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_EVEN_NA": {
        if (!target?.split("").every((char) => char === "0")) {
          return true;
        } else {
          return false;
        }
      }
      case "LENGTH_ODD": {
        if (target?.length % 2 !== 0) {
          return true;
        } else {
          return false;
        }
      }
    }

    return false;
  }

  getObjectByKey(key: string, elements: any, targetInstanceId = false) {
    if (elements) {
      for (const element of elements) {
        if (element.key === key) {
          return element;
        }

        if (element.screens) {
          for (const screen of element.screens) {
            if (screen.key === key) {
              return screen;
            }

            if (screen.inputs) {
              for (const input of screen.inputs) {
                if (input.key === key) {
                  return input;
                }
              }
            }
          }
        } else if (element.inputs) {
          for (const input of element.inputs) {
            if (input.key === key) {
              return input;
            }
          }
        } else if (element?.length) {
          for (const arrayInput of element) {
            if (targetInstanceId) {
              if (arrayInput.instanceId === key) {
                return arrayInput;
              }
            } else if (arrayInput.key === key) {
              return arrayInput;
            }
          }
        }
      }
    }

    return null;
  }

  isDistinct(input: ExpertModeElementScreenInput, elements: any, propertyName: string = null) {
    if (elements) {
      for (const element of elements) {
        let pointer = null;

        if (element.type === "array" || element.type === "object") {
          pointer = element.value; // Check values of an input
        } else {
          pointer = element.inputs; // Check inputs of a screen
        }

        if (pointer) {
          for (const screenInput of pointer) {
            if (screenInput.instanceId !== input.instanceId) {
              if (screenInput.value === input.value) {
                // If we're checking only for a propertyName target, only fail if this screenInput matches
                if (propertyName) {
                  if (screenInput.propertyName === propertyName) {
                    return false;
                  }
                } else {
                  return false;
                }
              }

              if ((screenInput.type === "array" || screenInput.type === "object") && screenInput.value?.length) {
                for (const arrayInput of screenInput.value) {
                  const distinct = this.isDistinct(input, arrayInput, propertyName);

                  if (!distinct) {
                    return false;
                  }
                }
              }
            }
          }
        } else if (element.screens) {
          for (const screen of element.screens) {
            const distinct = this.isDistinct(input, [screen], propertyName);

            if (!distinct) {
              return false;
            }
          }
        } else if (element?.length) {
          for (const arrayInput of element) {
            if (arrayInput.instanceId !== input.instanceId) {
              if (arrayInput.value === input.value) {
                // If we're checking only for a propertyName target, only fail if this screenInput matches
                if (propertyName) {
                  if (arrayInput.propertyName === propertyName) {
                    return false;
                  }
                } else {
                  return false;
                }
              }
            }
          }
        } else {
          if (element.instanceId !== input.instanceId) {
            if (element.value === input.value) {
              // If we're checking only for a propertyName target, only fail if this screenInput matches
              if (propertyName) {
                if (element.propertyName === propertyName) {
                  return false;
                }
              } else {
                return false;
              }
            }
          }
        }

        // If we get here, see if we need to perform a distinct crawl into nested complex types
        if (element.type === "array" || element.type === "object") {
          if (element.value) {
            for (const arrayRecord of element.value) {
              const distinct = this.isDistinct(input, arrayRecord, propertyName);

              if (!distinct) {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  }

  checkForDebugging(key: string, clearConsole = false) {
    if (this.onlyDebugKey) {
      if (key === this.onlyDebugKey) {
        if (clearConsole) {
          console.clear();
        }
        this.debugMode = true;
      } else {
        this.debugMode = false;
      }
    }
  }

  validateProfile(expertModeScreens: ExpertModeElement[]): ProfileValidityType {
    // performance.clearMarks();
    // performance.clearMeasures();
    // performance.mark("startValidation");

    let profileState: ProfileValidityType = "complete";
    let elementErrors = false;
    let elementIncompletes = false;

    for (const element of expertModeScreens) {
      let screenErrors = false;
      let screenIncompletes = false;

      for (const screen of element.screens) {
        let inputErrors = false;
        let inputIncompletes = false;

        if (screen.enabled) {
          for (const input of screen.inputs) {
            input.state = this.validateProfileNested(input, expertModeScreens);

            if (input.state === "error") {
              inputErrors = true;
            }
            if (input.state === "incomplete") {
              inputIncompletes = true;
            }
          }

          if (inputErrors) {
            screen.state = "error";
            screenErrors = true;
          } else if (inputIncompletes) {
            screen.state = "incomplete";
            screenIncompletes = true;
          } else {
            screen.state = "complete";
          }
        }
      }

      if (screenErrors) {
        element.state = "error";
        elementErrors = true;
      } else if (screenIncompletes) {
        element.state = "incomplete";
        elementIncompletes = true;
      } else {
        element.state = "complete";
      }
    }

    if (elementErrors) {
      profileState = "error";
    } else if (elementIncompletes) {
      profileState = "incomplete";
    }

    // performance.mark("endValidation");
    // performance.measure("validationTime", "startValidation", "endValidation");
    // const entries = performance.getEntriesByName("validationTime");
    // console.warn(`Validation time: ${entries[0].duration}ms`);

    return profileState;
  }

  validateProfileNested(input: ExpertModeElementScreenInput, expertModeScreens: ExpertModeElement[]): ProfileValidityType {
    if (input.type !== "displaytext") {
      if (input.type === "array" || input.type === "object") {
        if (input.value) {
          for (const record of input.value) {
            for (const recordInput of record) {
              const validationResult = this.validateInput(recordInput, expertModeScreens);

              if (!validationResult.valid) {
                return validationResult.validityType;
              }

              if (recordInput.type === "array" || recordInput.type === "object") {
                const inputState = this.validateProfileNested(recordInput, expertModeScreens);

                if (inputState === "error") {
                  return "error";
                } else if (inputState === "incomplete") {
                  return "incomplete";
                }
              }
            }
          }
        }
      }

      const validationResult = this.validateInput(input, expertModeScreens);

      return validationResult.validityType;
    }

    return null;
  }

  enableCheckInputs(expertModeScreens: ExpertModeElement[]) {
    for (const element of expertModeScreens) {
      for (const screen of element.screens) {
        screen.enabled = this.enableCheckScreen(screen, expertModeScreens);

        for (const input of screen.inputs) {
          this.enableCheckInputsNested(input, expertModeScreens);
        }
      }
    }
  }

  enableCheckInputsNested(input: ExpertModeElementScreenInput, expertModeScreens: ExpertModeElement[]) {
    if (input.type === "array" || input.type === "object") {
      if (input.value) {
        for (const record of input.value) {
          for (const recordInput of record) {
            recordInput.enabled = this.enableCheckInput(recordInput, expertModeScreens);
            if (recordInput.type === "array" || recordInput.type === "object") {
              this.enableCheckInputsNested(recordInput, expertModeScreens);
            }
          }
        }
      }
    }

    input.enabled = this.enableCheckInput(input, expertModeScreens);
  }

  getInputScope(
    input: ExpertModeElementScreenInput,
    elements: ExpertModeElement[],
    scope: ProfileElementScopeType
  ): ExpertValidatorServiceInputScopes {
    let inputElementScope = null;
    let inputScreenScope = null;
    let inputArrayScope = null;
    let inputArrayRecordScope = null;

    for (const element of elements) {
      for (const elementScreen of element.screens) {
        // Special condition when we are processing scoping for screen conditionals (opposed to inputs)
        if (input.type === null && input.key === elementScreen.key) {
          inputElementScope = [element];
          inputScreenScope = [elementScreen];
          break;
        } else {
          for (const elementScreenInput of elementScreen.inputs) {
            if (input.instanceId === elementScreenInput.instanceId) {
              inputElementScope = [element];
              inputScreenScope = [elementScreen];
              break;
            } else if (elementScreenInput.type === "array" || elementScreenInput.type === "object") {
              // In the case of an array, look into all its values to see if we can find the instanceId there, as it may be an input of an array
              const inputScope = this.resolveInputScopeNested(input.instanceId, elementScreenInput);

              if (inputScope) {
                inputElementScope = [element];
                inputScreenScope = [elementScreen];
                inputArrayScope = inputScope;

                if (elementScreenInput.type === "array" && scope === "arrayrecord") {
                  for (const arrayRecord of inputArrayScope) {
                    for (const arrayRecordInput of arrayRecord) {
                      if (arrayRecordInput.instanceId === input.instanceId) {
                        inputArrayRecordScope = arrayRecord;

                        break;
                      }
                    }
                  }
                }

                break;
              }
            }
          }
        }
      }
    }

    let actualScope = null;

    if (scope) {
      if (scope === "global") {
        actualScope = elements;
      } else if (scope === "element") {
        actualScope = inputElementScope;
      } else if (scope === "screen") {
        actualScope = inputScreenScope;
      } else if (scope === "array") {
        actualScope = inputArrayScope;
      } else if (scope === "arrayrecord") {
        actualScope = inputArrayRecordScope;
      }
    } else {
      // If we have found array scope and we're resorting to default scope, for arrays this is the array scope
      if (inputArrayScope) {
        actualScope = inputArrayScope;
      } else {
        actualScope = inputScreenScope;
      }
    }

    return {
      globalScope: elements,
      elementScope: inputElementScope,
      screenScope: inputScreenScope,
      arrayScope: inputArrayScope,
      scope: actualScope
    };
  }

  seedOptionsSource(expertModeScreens: ExpertModeElement[]): boolean {
    let inputValueChanged = false;

    for (const element of expertModeScreens) {
      for (const screen of element.screens) {
        for (const input of screen.inputs) {
          const valueChanged = this.seedOptionsSourceNested(input, expertModeScreens);

          if (valueChanged) {
            inputValueChanged = true;
          }
        }
      }
    }

    return inputValueChanged;
  }

  seedOptionsSourceNested(input: ExpertModeElementScreenInput, expertModeScreens: ExpertModeElement[]): boolean {
    let inputValueChanged = false;

    if (input.type !== "displaytext") {
      if (input.type === "array" || input.type === "object") {
        if (input.value) {
          for (const record of input.value) {
            for (const recordInput of record) {
              if (recordInput.type === "array" || recordInput.type === "object") {
                const valueChanged = this.seedOptionsSourceNested(recordInput, expertModeScreens);

                if (valueChanged) {
                  inputValueChanged = true;
                }
              } else {
                const valueChanged = this.seedOptionsSourceForInput(recordInput, expertModeScreens);

                if (valueChanged) {
                  inputValueChanged = true;
                }
              }
            }
          }
        }
      } else {
        const valueChanged = this.seedOptionsSourceForInput(input, expertModeScreens);

        if (valueChanged) {
          inputValueChanged = true;
        }
      }
    }

    return inputValueChanged;
  }

  seedOptionsSourceForInput(input: ExpertModeElementScreenInput, expertModeScreens: ExpertModeElement[]): boolean {
    let inputValueChanged = false;

    if ((input.type === "dropdown" || input.type === "radio") && input.optionsSource) {
      const inputScope = this.getInputScope(input, expertModeScreens, input.optionsSource.scope);
      const newOptions = [];
      const values = this.getOptionsSourceValues(
        input.optionsSource.key ?? null,
        input.optionsSource.propertyName ?? null,
        inputScope.scope
      );

      for (const value of values) {
        let addValue = true;

        if (!input.optionsSource.ignoreDuplicates) {
          addValue = true;
        } else {
          for (const option of newOptions) {
            if (option.value === value) {
              addValue = false;
              break;
            }
          }
        }

        if (addValue) {
          newOptions.push({
            label: value.label,
            value: value.value
          });
        }
      }

      newOptions.sort((a, b) => {
        if (typeof a.label === "string" && typeof b.label === "string") {
          if (a.label.toLowerCase() > b.label.toLowerCase()) {
            return 1;
          } else {
            return -1;
          }
        } else {
          if (a > b) {
            return 1;
          } else {
            return -1;
          }
        }
      });

      const newOptionsSorted = [];

      for (const option of newOptions) {
        newOptionsSorted.push({
          label: option.label,
          value: option.value
        });
      }

      input.options = [...newOptionsSorted];

      // If the input has a value, ensure it is in the list of options, otherwise set it to null
      if (input.value) {
        let valueFound = false;

        for (const option of newOptions) {
          if (option.value === input.value) {
            valueFound = true;
            break;
          }
        }

        if (!valueFound) {
          input.value = null;
          inputValueChanged = true;
        }
      }
    }

    return inputValueChanged;
  }

  getOptionsSourceValues(key: string, propertyName: string, scope: any, values: any[] = []): any[] {
    function isValidOptionSource(input: any): boolean {
      if (
        input.value &&
        input.enabled &&
        (input.type === "number" || input.type === "string" || input.type === "dropdown" || input.type === "radio")
      ) {
        return true;
      } else {
        return false;
      }
    }

    function isNestedInputType(input: any): boolean {
      if (input.type === "array" || input.type === "object") {
        return true;
      } else {
        return false;
      }
    }

    if (scope) {
      for (const record of scope) {
        if (record.screens) {
          for (const screen of record.screens) {
            this.getOptionsSourceValues(key, propertyName, [screen], values);
          }
        } else if (record.inputs) {
          for (const input of record.inputs) {
            if (isNestedInputType(input) && input.value) {
              for (const arrayRecord of input.value) {
                for (const arrayValue of arrayRecord) {
                  this.getOptionsSourceValues(key, propertyName, [arrayValue], values);
                }
              }
            } else {
              if ((key && input.key === key) || (input.propertyName === propertyName && isValidOptionSource(input))) {
                if (input.type === "dropdown" || input.type === "radio") {
                  if (input.options) {
                    for (const option of input.options) {
                      if (option.value === input.value) {
                        values.push({ label: option.label, value: input.value });
                        break;
                      }
                    }
                  }
                } else {
                  values.push({ label: input.value, value: input.value });
                }
              }
            }
          }
        } else {
          if (isNestedInputType(record) && record.value) {
            for (const arrayRecord of record.value) {
              for (const arrayValue of arrayRecord) {
                this.getOptionsSourceValues(key, propertyName, [arrayValue], values);
              }
            }
          } else {
            if ((key && record.key === key) || (record.propertyName === propertyName && isValidOptionSource(record))) {
              if (record.type === "dropdown" || record.type === "radio") {
                if (record.options) {
                  for (const option of record.options) {
                    if (option.value === record.value) {
                      values.push({ label: option.label, value: record.value });
                      break;
                    }
                  }
                }
              } else {
                values.push({ label: record.value, value: record.value });
              }
            }
          }
        }
      }
    }

    return values;
  }

  buildProfileState(expertModeScreens) {
    // performance.clearMarks();
    // performance.clearMeasures();
    // performance.mark("startValidation");

    const beforeState = JSON.stringify(expertModeScreens);
    this.enableCheckInputs(expertModeScreens);
    let expertModeProfileOverallState = this.validateProfile(expertModeScreens);
    const valueChanged = this.seedOptionsSource(expertModeScreens);

    // If seeding option sources has resulted in changed an input's value, the whole process must be rerun to pick up any side effects
    if (valueChanged) {
      expertModeProfileOverallState = this.buildProfileState(expertModeScreens);
    } else {
      const afterState = JSON.stringify(expertModeScreens);

      if (beforeState !== afterState) {
        expertModeProfileOverallState = this.buildProfileState(expertModeScreens);
      }
    }

    // performance.mark("endValidation");
    // performance.measure("validationTime", "startValidation", "endValidation");
    // const entries = performance.getEntriesByName("validationTime");
    // console.warn(`Validation time: ${entries[0].duration}ms`);

    return expertModeProfileOverallState;
  }

  removeComments(jsonString: string) {
    return jsonString.replace(/\\"|"(?:\\"|[^"])*"|(\/\/.*|\/\*[\s\S]*?\*\/)/g, (m, g) => (g ? "" : m));
  }
}
